import {RefObject, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {css, Theme} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {backgroundColor, flex, flexAlignCenter, onDesktop, order, p, zIndex} from "@pg-design/helpers-css";
import {BrandSearchIcon, ChevronDownIcon, CloseIcon} from "@pg-design/icons";
import {Text} from "@pg-design/text";

import {useAppDispatch, useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {useClickOutside} from "../../../../../utils/hooks/useClickOutside";
import {ZIndex} from "../../../common/app/constants/z_index";
import {isServer} from "../../../common/app/read_charts_web_environment_variables";
import {rotate} from "../../css_helpers/rotate";
import {useGetRegionAutocompleteQuery} from "../../map/api/charts_regions_api";
import {clearChartsRegionPoc, selectChartsSelectedRegion} from "../../map/redux/map_city_poc_slice";
import {Tooltip} from "../../Tooltip";
import {useRecentMapRegionSearches} from "../hooks/use_recent_map_region_searches";
import {
    applyRegionSelection,
    mapRegionsToOptions,
    PERMITTED_REGION_AUTOCOMPLETE_ARGS,
    sortPermittedRegionOptions
} from "../utils/map_region_options";
import {MapRegionSearchOption, toRecentMapRegionSearchInput} from "../utils/map_region_search_option";
import {MapRegionOptionsSection} from "./MapRegionOptionsSection";
import {MapRegionRecentSearches} from "./MapRegionRecentSearches";

const SEARCH_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

export const CityMapAutoselect = () => {
    const dispatch = useAppDispatch();
    const chartsSelectedRegion = useAppSelector(selectChartsSelectedRegion);
    const {recentSearches, addRecentSearch} = useRecentMapRegionSearches();
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedQuery, setDebouncedQuery] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const {t} = useTranslation();

    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedQuery(searchQuery.trim()), SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [searchQuery]);

    const {data: searchResults = [], isFetching: isSearchFetching} = useGetRegionAutocompleteQuery(
        {q: debouncedQuery},
        {skip: debouncedQuery.length < SEARCH_MIN_LENGTH}
    );

    const isEmptyQuery = searchQuery.trim().length === 0;

    const {
        data: permittedResults = [],
        isFetching: isPermittedFetching,
        isUninitialized: isPermittedUninitialized
    } = useGetRegionAutocompleteQuery(PERMITTED_REGION_AUTOCOMPLETE_ARGS, {
        skip: isServer,
        refetchOnMountOrArgChange: true
    });

    const isPermittedListLoading = isPermittedUninitialized || isPermittedFetching;

    const dropdownOptions = useMemo(() => mapRegionsToOptions(searchResults), [searchResults]);
    const permittedOptions = useMemo(
        () => sortPermittedRegionOptions(mapRegionsToOptions(permittedResults)),
        [permittedResults]
    );

    const selectedLabel = chartsSelectedRegion?.label ?? "";
    const inputValue = isDropdownOpen ? searchQuery : selectedLabel;

    const resetSearch = () => {
        setSearchQuery("");
        setDebouncedQuery("");
    };

    const closeList = () => {
        setIsDropdownOpen(false);
        resetSearch();
    };

    useClickOutside(wrapperRef as RefObject<HTMLElement>, closeList);

    const selectRegion = (option: MapRegionSearchOption) => {
        resetSearch();
        setIsDropdownOpen(false);
        applyRegionSelection(dispatch, option.region, option.label, option.citySlug);
    };

    const handleSelectOption = (option: MapRegionSearchOption) => {
        selectRegion(option);
        addRecentSearch(toRecentMapRegionSearchInput(option));
    };

    const openList = () => {
        resetSearch();
        setIsDropdownOpen(true);
    };

    const toggleList = () => {
        if (isDropdownOpen) {
            closeList();
            inputRef.current?.blur();
            return;
        }

        openList();
        inputRef.current?.focus();
    };

    const handleClear = () => {
        if (searchQuery.trim().length > 0) {
            resetSearch();
            setIsDropdownOpen(true);
            inputRef.current?.focus();
            return;
        }

        dispatch(clearChartsRegionPoc());
        resetSearch();
        setIsDropdownOpen(true);
        inputRef.current?.focus();
    };

    const showDropdown = isDropdownOpen && debouncedQuery.length >= SEARCH_MIN_LENGTH;
    const showSearchHint =
        isDropdownOpen && searchQuery.trim().length > 0 && searchQuery.trim().length < SEARCH_MIN_LENGTH;
    const showRecent = isDropdownOpen && isEmptyQuery && recentSearches.length > 0;
    const showPermitted = isDropdownOpen && isEmptyQuery;
    const showClearButton = searchQuery.trim().length > 0 || chartsSelectedRegion != null;
    const clearAriaLabel =
        searchQuery.trim().length > 0 ? t("nav.map_region_clear_search") : t("nav.map_region_clear_selection");
    const isPanelOpen = showDropdown || showSearchHint || showRecent || showPermitted;

    return (
        <div css={wrapper} ref={wrapperRef}>
            <div css={[searchField, isPanelOpen && searchFieldFocused]}>
                <Tooltip
                    isServerSideRendered
                    config={{trigger: "hover", placement: "bottom"}}
                    triggerClassName={searchIcon}
                    trigger={<BrandSearchIcon css={searchIconColor} size="2.4" />}
                    tooltipClassName={searchIconTooltip}
                >
                    <Text variant="info_txt_1" as="span">
                        {t("nav.map_region_search_icon_tooltip")}
                    </Text>
                </Tooltip>

                <input
                    ref={inputRef}
                    css={inputStyle}
                    type="text"
                    value={inputValue}
                    placeholder={t("nav.map_region_search_placeholder")}
                    aria-label={t("nav.choose_city")}
                    aria-expanded={isPanelOpen}
                    aria-autocomplete="list"
                    role="combobox"
                    onChange={(event) => {
                        setSearchQuery(event.target.value);
                        setIsDropdownOpen(true);
                    }}
                    onFocus={openList}
                    onKeyDown={(event) => {
                        if (event.key === "Escape") {
                            closeList();
                            inputRef.current?.blur();
                        }
                    }}
                />

                {showClearButton ? (
                    <button
                        type="button"
                        css={iconButton}
                        aria-label={clearAriaLabel}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={handleClear}
                    >
                        <CloseIcon size="2" />
                    </button>
                ) : null}

                <button
                    type="button"
                    css={iconButton}
                    aria-label={isPanelOpen ? t("nav.map_region_close_list") : t("nav.map_region_open_list")}
                    aria-expanded={isPanelOpen}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={toggleList}
                >
                    <span css={[chevronIcon, isPanelOpen && chevronIconOpen]} aria-hidden>
                        <ChevronDownIcon size="2" />
                    </span>
                </button>
            </div>

            {isPanelOpen ? (
                <div css={dropdownPanel}>
                    {showSearchHint ? <div css={dropdownHint}>{t("nav.map_region_select_poc_type_more")}</div> : null}

                    {showRecent ? (
                        <MapRegionRecentSearches recentSearches={recentSearches} onSelect={handleSelectOption} />
                    ) : null}

                    {showPermitted ? (
                        <MapRegionOptionsSection
                            title={t("nav.map_region_available_locations")}
                            options={permittedOptions}
                            onSelect={handleSelectOption}
                            isLoading={isPermittedListLoading}
                            emptyMessage={t("nav.map_region_select_poc_no_results")}
                        />
                    ) : null}

                    {showDropdown ? (
                        <MapRegionOptionsSection
                            title={t("nav.map_region_search_results")}
                            options={dropdownOptions}
                            onSelect={handleSelectOption}
                            isLoading={isSearchFetching}
                            emptyMessage={t("nav.map_region_select_poc_no_results")}
                        />
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

const wrapper = css`
    ${order(2)};
    position: relative;
    width: 100%;
    min-width: 0;

    ${onDesktop(css`
        width: auto;
        min-width: 520px;
        max-width: 720px;
        flex: 1 1 520px;
    `)};
`;

const searchField = (theme: Theme) => css`
    position: relative;
    ${flexAlignCenter};
    width: 100%;
    min-height: 4.25rem;
    padding: 0 1rem 0 0.5rem;
    background: #fff;
    border: 1px solid ${theme.colors.gray[300]};
    border-radius: 999px;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
`;

const searchFieldFocused = (theme: Theme) => css`
    border-color: ${theme.colors.gray[400]};
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
`;

const searchIcon = css`
    ${flex("center", "center")};
    flex-shrink: 0;
    width: 3.5rem;
    cursor: help;
`;

const searchIconColor = (theme: Theme) => css`
    color: ${theme.colors.gray[600]};
`;

const searchIconTooltip = css`
    ${p(1.5, 2)};
    ${zIndex(ZIndex.HIGHCHARTS_TOOLTIP)};
    ${backgroundColor("#fff")};
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
`;

const inputStyle = (theme: Theme) => css`
    width: 100%;
    min-width: 0;
    padding: 1rem 0.75rem;
    border: none;
    background: transparent;
    font-size: 1.125rem;
    line-height: 1.4;
    color: ${theme.colors.gray[800]};

    &::placeholder {
        color: ${theme.colors.gray[600]};
    }

    &:focus {
        outline: none;
    }
`;

const iconButton = (theme: Theme) => css`
    ${flex("center", "center")};
    flex-shrink: 0;
    width: 2.5rem;
    height: 2.5rem;
    margin-right: 0.25rem;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: ${theme.colors.gray[600]};
    cursor: pointer;

    &:hover {
        background: ${theme.colors.gray[100]};
        color: ${theme.colors.gray[800]};
    }
`;

const chevronIcon = css`
    display: flex;
    transition: rotate 0.15s ease;
`;

const chevronIconOpen = css`
    ${rotate("180")};
`;

const dropdownPanel = (theme: Theme) => css`
    position: absolute;
    z-index: 200;
    top: calc(100% + 0.75rem);
    left: 0;
    right: 0;
    max-height: min(56rem, 70vh);
    overflow-x: hidden;
    overflow-y: auto;
    background: #fff;
    border: 1px solid ${theme.colors.gray[200]};
    border-radius: 1rem;
    ${elevation(2)};
`;

const dropdownHint = (theme: Theme) => css`
    padding: 1.25rem 1.5rem;
    font-size: 1rem;
    color: ${theme.colors.gray[600]};
`;
